import {
  createSelector,
  createSelectorCreator,
  lruMemoize,
} from 'reselect';
import isEqual from 'lodash.isequal';
import {
  AMERICAN_ODDS,
  DECIMAL_ODDS,
  FRACTIONAL_ODDS,
} from 'constants/bcms';
import {
  getOddsFormat,
  getMatchCurPeriod,
  getMatchSportId,
  getBId,
  getLanguage,
  getClientAlias,
  getTranslations,
  getIsTranslationsAvailable,
  getMatchStatus,
} from 'reducers/index';
import formatOdds from 'odds-conversion/index';
import clientRanking from 'ClientsBranding/configs/marketRanking';
import { getLocalMarketName, ordinalSuffixOf } from 'localization';
import { isEmpty } from 'lodash';
import {
  getAllOddsTypes,
  oddsTypeRankingUOF,
  sport,
  goalsHomeAwayTeamMarketsUOF,
  oddsTypeUOF,
} from 'constants/enum';
import createIntlHelper from 'localization/intlInitHelper';
import { isMarketInCollection } from 'reducers/fishnetHelpers';
import { isLive } from 'utils/matchstatus';

const equalEnough = (arr, arrTmp) => (
  arr.length === arrTmp.length
  && arrTmp.every((mN, i) => mN === arr[i])
    ? arr
    : arrTmp
);

// JANG, SU JEONG => JANG S J
const doubleShortening = name => {
  try {
    if (name.includes(',')) {
      const idx = name.indexOf(',');
      let afterComma = name.split(', ')[1];
      afterComma = afterComma.split(' ').map(i => i.charAt(0)).join(' ');
      return name.slice(0, idx) + ' ' + afterComma;
    }
    return name;
  } catch (error) {
    return name;
  }
};

const createDeepEqualSelector = createSelectorCreator(lruMemoize, isEqual);

// merge team names to odds and select rigth oddsformat for client
const finalOutcomesOutput = (odds, outcomeNames, oddsFormat, betstop, keepAllOutcomes, sportId) => {
  if (!odds || !odds.length) {
    return [];
  }

  const oddsArr = Object.values(odds);
  // will count as active/shown
  if (oddsArr.every(outcome => outcome.odds === '-' || outcome.odds <= 0)) {
    return [];
  }

  let arr = [];
  for (let i = 0; i < oddsArr.length; i++) {
    const outcome = { ...oddsArr[i] };
    outcome.feedOdds = outcome.odds;
    outcome.feedName = outcome.name;
    outcome.decOdds = parseFloat(outcome.odds);

    if (betstop || !outcome.odds) {
      outcome.odds = '-';
    } else if (oddsFormat === AMERICAN_ODDS) {
      outcome.odds = formatOdds(outcome.odds, AMERICAN_ODDS);
    } else if (oddsFormat === FRACTIONAL_ODDS) {
      outcome.odds = formatOdds(outcome.odds, FRACTIONAL_ODDS);
    } else {
      outcome.odds = formatOdds(outcome.odds, DECIMAL_ODDS);
    }
    // Replacing placeholders in outcome name with team names
    // Replacing placeholders in outcomes with hcp values
    if (
      !outcomeNames
      || isMarketInCollection(goalsHomeAwayTeamMarketsUOF, outcome.marketId) // use feed names
    ) {
      // do nothing
    } else if (typeof outcome.totalHcp === 'number') {
      outcome.abbr = outcome.name.replace('{total}', outcome.totalHcp);
      outcome.name = outcome.name.replace('{total}', outcome.totalHcp);
    } else if (typeof outcome.spreadHcp === 'number') {
      const hcp = ' (' + (outcome.spreadHcp > 0 ? '+' + outcome.spreadHcp : outcome.spreadHcp) + ')';
      outcome.abbr = outcomeNames.abbr[i] + hcp;
      outcome.name = outcomeNames.name[i] + hcp;
    } else if (outcome.name === '{$competitor1}' && isEmpty(outcomeNames) && sportId === sport.table_tennis) {
      // @TODO: can be added to TAPI
      outcome.name = 'Home';
      outcome.abbr = 'Home';
    } else if (outcome.name === '{$competitor2}' && isEmpty(outcomeNames) && sportId === sport.table_tennis) {
      // @TODO: can be added to TAPI
      outcome.name = 'Away';
      outcome.abbr = 'Away';
    } else if (outcome.name === '{$competitor1}') {
      /* eslint-disable prefer-destructuring */
      outcome.name = outcomeNames.name[0];
      outcome.abbr = outcomeNames.abbr[0];
    } else if (outcome.name === '{$competitor2}') {
      outcome.name = outcomeNames.name[1];
      outcome.abbr = outcomeNames.abbr[1];
      /* eslint-enable  prefer-destructuring */
    } else if (outcome.name.includes('{$competitor1}') || outcome.name.includes('{$competitor2}')) {
      outcome.name = outcome.name.replace('{$competitor1}', outcomeNames.abbr[0]);
      outcome.name = outcome.name.replace('{$competitor2}', outcomeNames.abbr[1]);
    }

    // Replace placeholders with handicap values and keeping desired prefix format (eq. +1.5, -2.0, or 1.5)
    if (outcome.otherHcp) {
      const hcp = outcome.otherHcp;
      outcome.name = outcome.name
        .replace('{+hcp}', `+${hcp}`)
        .replace('{-hcp}', `-${hcp}`)
        .replace('{hcp}', `${hcp}`);
    } else {
      outcome.name = outcome.name
        .replace('({+hcp})', '')
        .replace('({-hcp})', '')
        .replace(/(\({hcp}\))+/g, '');
    }

    arr.push(outcome);
  }

  // dont sort odds for team goals market
  if (
    arr.length > 3
    && !arr.some(
      o => isMarketInCollection(goalsHomeAwayTeamMarketsUOF, o.marketId),
    )
  ) {
    arr = arr.filter(outcome => outcome.odds !== '-');
    arr.sort((a, b) => ((a.decOdds > b.decOdds) ? 1 : -1));
  }
  if (!keepAllOutcomes) {
    arr = arr.slice(0, 3);
  }

  return arr;
};

// insert dynamic values into market name
const shortMarketNameOutput = (
  marketName, localMarketName, extra, curPeriod, hcp, ahcp, betstop, uofHcp, goalNr,
) => {
  let oTypeShort = localMarketName || marketName;
  const localExtra = extra || ''; // avoid using str func on non string

  if (
    (oTypeShort.includes('$x') && oTypeShort.includes('$y'))
    || (oTypeShort.includes('{extra88:gameX}') && oTypeShort.includes('{extra88:gameY}'))
  ) {
    // oddsid 88 "Win games $x and $y"
    let and = localExtra.toLowerCase().indexOf('and');
    if (and < 0) {
      and = '';
    }
    const game1 = localExtra.substring(2, and);
    const game2 = localExtra.substring(and + 3);

    oTypeShort = oTypeShort.replace('$x', game1);
    oTypeShort = oTypeShort.replace('$y', game2);
    oTypeShort = oTypeShort.replace('{extra88:gameX}', game1);
    oTypeShort = oTypeShort.replace('{extra88:gameY}', game2);
  }
  if (
    (oTypeShort.includes('{!gamenrX}') && oTypeShort.includes('{!gamenrY}'))
  ) {
    // oddsid 209 "Win games $x and $y"
    let and = localExtra.toLowerCase().indexOf('and');
    if (and < 0) {
      and = '';
    }
    oTypeShort = oTypeShort.replace('{!gamenrX}', uofHcp.gamenrX);
    oTypeShort = oTypeShort.replace('{!gamenrY}', uofHcp.gamenrY);
  }
  if (oTypeShort.includes('$x')) {
    // should only use curPeriod when localM is used
    oTypeShort = oTypeShort.replace('$x', betstop ? '-' : (extra || curPeriod));
  }
  if (oTypeShort.includes('{extra}')) {
    // from extra prop in fishnet feed
    oTypeShort = oTypeShort.replace('{extra}', extra || '-');
  }
  if (oTypeShort.includes('{period}')) {
    oTypeShort = oTypeShort.replace('{period}', betstop ? '-' : curPeriod);
  }
  if (oTypeShort.includes('{pSuffix}')) {
    oTypeShort = oTypeShort.replace('{pSuffix}', betstop ? '-' : ordinalSuffixOf(curPeriod));
  }
  if (oTypeShort.includes('{hcp}')) {
    oTypeShort = oTypeShort.replace('{hcp}', hcp);
  }
  if (oTypeShort.includes('{ahcp}')) {
    const hcpReplacement = ahcp || uofHcp.total || '';
    oTypeShort = oTypeShort.replace('{ahcp}', hcpReplacement);
  }
  if (oTypeShort.includes('{!goalnr}')) {
    oTypeShort = oTypeShort.replace('{!goalnr}', goalNr || uofHcp.goalnr);
  }
  if (oTypeShort.includes('{!setnr}')) {
    oTypeShort = oTypeShort.replace('{!setnr}', uofHcp.setnr);
  }
  if (oTypeShort.includes('{!quarternr}')) {
    oTypeShort = oTypeShort.replace('{!quarternr}', uofHcp.quarternr);
  }
  if (oTypeShort.includes('{gamenr}')) {
    oTypeShort = oTypeShort.replace('{gamenr}', uofHcp.gamenr);
  }
  if (oTypeShort.includes('{!pointnr}') || (oTypeShort.includes('{pointnr}'))) {
    oTypeShort = oTypeShort.replace('{!pointnr}', uofHcp.pointnr);
    oTypeShort = oTypeShort.replace('{pointnr}', uofHcp.pointnr);
  }
  if (oTypeShort.includes('{!periodnr}')) {
    oTypeShort = oTypeShort.replace('{!periodnr}', uofHcp.periodnr);
  }

  // remove unreplaced {var}
  oTypeShort = oTypeShort.replace(/{.*?}/, '');

  return oTypeShort;
};

// array of odds objects for one market
const oddsOutputSelector = (odds, oddsIds) => oddsIds.map(oddsId => odds[oddsId]);

export class MarketProjection {
  constructor(
    state,
    matchId,
    marketId,
    sportId,
    lang,
    bId,
    srAlias,
    keepAllOutcomes,
    translations,
    intl,
  ) {
    this.id = matchId;
    this.state = state;
    this.marketId = marketId;
    this.sportId = sportId;
    this.lang = lang;
    this.bId = bId;
    this.srAlias = srAlias;
    this.keepAllOutcomes = keepAllOutcomes;
    this.translations = translations;
    this.intl = intl;
  }
  // private instances of memoized selectors

  // array of oddsIds for one market
  oddsIdsByMarketSelector = createSelector(
    [
      state => state,
      (state, matchId) => matchId,
      (state, matchId, marketId) => marketId,
    ],
    (state, matchId, marketId) => {
      if (
        !state.markets
        || !state.markets.matches
        || !state.markets.matches[matchId]
        || !state.markets.matches[matchId].oddsIdsByMarket[marketId]
      ) {
        return [];
      }

      return state.markets.matches[matchId].oddsIdsByMarket[marketId]; // [0]
    },
  );


  // gets array of odds objects for one market
  oddsSelctor = createSelector(
    [
      state => state.markets.odds,
      this.oddsIdsByMarketSelector,
    ],
    oddsOutputSelector,
  );

  localMarketNameSelector = createSelector(
    [
      (state, matchId, marketId) => marketId,
      (state, matchId, marketId, curPeriod, sportId) => sportId,
      (state, matchId, marketId, curPeriod, sportId, lang) => lang,
      (state, matchId, marketId, curPeriod, sportId, lang, srAlias) => srAlias,
      (state, matchId, marketId, curPeriod, sportId, lang, srAlias, translations) => translations,
      (state, matchId, marketId, curPeriod, sportId, lang, srAlias, translations, intl) => intl,
      (state, matchId, marketId) => {
        const id = matchId + '_' + marketId;
        if (
          state
          && state.bigMarket
          && state.bigMarket[id]
          && typeof state.bigMarket[id].type !== 'undefined'
        ) {
          return state.bigMarket[id].type;
        }
        return false;
      },
    ],
    // should just be run once
    (
      marketId,
      sportId,
      lang,
      srAlias,
      translations,
      intl,
      type,
    ) => getLocalMarketName(marketId, sportId, lang, srAlias, translations, intl, type),
  );

  // gets extra property, only not null for dynamic marketnames
  extrasSelector = createSelector(
    [
      state => state.extras,
      (state, matchId) => matchId,
      (state, matchId, marketId) => marketId,
    ],
    (extras, matchId, marketId) => (extras ? extras[matchId + '_' + marketId] : null),
  );

  // get hcp, only cares about hcp in form (0:1) not -1.5
  hcpSelector = createSelector(
    [
      (state, matchId, marketId) => (
        state.bigMarket
          ? state.bigMarket[matchId + '_' + marketId]
          : null),
    ],
    market => (
      market
      && market.hcp
      && typeof market.hcp.hcp === 'string'
      && market.hcp.hcp.includes(':')
        ? market.hcp.hcp
        : null),
  );

  uofHcpSelector = createSelector(
    [
      (state, matchId, marketId) => (
        state.bigMarket
          ? state.bigMarket[matchId + '_' + marketId]
          : null),
    ],
    market => (
      market
      && market.hcp
        ? market.hcp
        : null),
  );

  // get ahcp, only cares about hcp in form -1.5 or total 2.5
  ahcpSelector = createSelector(
    [
      (state, matchId, marketId) => (
        state.bigMarket
          ? state.bigMarket[matchId + '_' + marketId]
          : null),
    ],
    market => (
      market
      && market.hcp
      && typeof market.hcp.hcp !== 'string'
        ? market.hcp.hcp
        : null),
  );

  // get betstop
  betstopSelector = createSelector(
    (state, matchId, marketId) => {
      // given different levels of state
      if (state && state.bigMarket) {
        return state.bigMarket[matchId + '_' + marketId];
      } else if (state && state.markets.bigMarket) {
        return state.markets.bigMarket[matchId + '_' + marketId];
      }
      return null;
    },
    // state => state,
    market => (market ? market.betstop : null),
  );

  goalNrSelector = createSelector(
    (state = this.state.fishnetFeeds, matchId = this.matchId) => {
      if (this.sportId !== sport.soccer) {
        return null;
      }
      const match = state.match[matchId];
      return match && match.result
        ? (match.result.home + match.result.away + 1)
        : null;
    },
  );

  // gets oddsTypeShort prop
  marketNameShortSelectorPure = createSelector(
    [
      state => state.marketLookup,
      (state, matchId, marketId) => marketId,
    ],
    (marketLookup, marketId) => (marketLookup[marketId] ? marketLookup[marketId].marketNameShort : ''),
  );

  // add dynamic values to marketname
  marketNameShortSelector = createSelector(
    [
      this.marketNameShortSelectorPure,
      this.localMarketNameSelector,
      this.extrasSelector,
      (state, matchId, marketId, curPeriod) => curPeriod,
      this.hcpSelector,
      this.ahcpSelector,
      this.betstopSelector,
      this.uofHcpSelector,
      this.goalNrSelector,
    ],
    shortMarketNameOutput,
  );

  // gets oddsType prop
  marketNameSelector = createSelector(
    [
      state => state.marketLookup,
      (state, matchId, marketId) => marketId,
    ],
    (marketLookup, marketId) => (marketLookup[marketId] ? marketLookup[marketId].marketName : ''),
  );

  // check if market has odds
  outcomesLengthSelector = createSelector(
    this.oddsIdsByMarketSelector,
    ids => ids.length,
  );


  // get team names from matchinfo feed, move into fishnet reducerfile?
  // is run every matchinfo update (but not timelinedelta)
  // will be deep equal checked
  outcomeNamesSelector = createSelector(
    (state, matchId) => (state.matchInfo[matchId] ? state.matchInfo[matchId].teams : null),

    teams => {
      if (!teams) {
        return null;
      }
      const retObj = {};

      if (teams.home.length === 1 && teams.away.length === 1) {
        retObj.abbr = [teams.home[0].abbr, teams.away[0].abbr];
        retObj.name = [teams.home[0].name, teams.away[0].name];
      } else if (teams.home.length === 2 && teams.away.length === 2) {
        retObj.abbr = [
          teams.home[0].abbr + ' / ' + teams.home[1].abbr,
          teams.away[0].abbr + ' / ' + teams.away[1].abbr,
        ];
        retObj.name = [
          doubleShortening(teams.home[0].name) + ' / ' + doubleShortening(teams.home[1].name),
          doubleShortening(teams.away[0].name) + ' / ' + doubleShortening(teams.away[1].name),
        ];
      }

      return retObj;
    },
  );

  feedOutcomesSelector = createDeepEqualSelector(
    [
      this.oddsSelctor,
      () => null,
      (state, matchId, marketId, oddsFormat) => oddsFormat,
      this.betstopSelector,
      (state, matchId, marketId, oddsFormat, keepAllOutcomes) => keepAllOutcomes,
    ],
    // eslint-disable-next-line max-len
    (odds, _, oddsFormat, betstop, keepAllOutcomes) => finalOutcomesOutput(odds, null, oddsFormat, betstop, keepAllOutcomes, this.sportId),
  );

  // do deep equal check to not trigger rerender if e.g. odds didnt change
  outcomesSelector = createDeepEqualSelector(
    [
      this.oddsSelctor,
      this.outcomeNamesSelector,
      (state, matchId, marketId, oddsFormat) => oddsFormat,
      this.betstopSelector,
      (state, matchId, marketId, oddsFormat, keepAllOutcomes) => keepAllOutcomes,
    ],
    // eslint-disable-next-line max-len
    (odds, outcomeNames, oddsFormat, betstop, keepAllOutcomes) => finalOutcomesOutput(odds, outcomeNames, oddsFormat, betstop, keepAllOutcomes, this.sportId),
  );

  // update state
  update(state, matchId, marketId, sportId) {
    this.state = state;
    this.matchId = matchId;
    this.marketId = marketId;
    this.sportId = sportId;
  }

  get outcomes() {
    return this.outcomesSelector(
      this.state.fishnetFeeds,
      this.matchId,
      this.marketId,
      getOddsFormat(this.state),
      this.keepAllOutcomes,
    );
  }

  get feedOutcomes() {
    return this.feedOutcomesSelector(
      this.state.fishnetFeeds,
      this.matchId,
      this.marketId,
      getOddsFormat(this.state),
      this.keepAllOutcomes,
    );
  }

  get outcomeNames() {
    return this.outcomeNamesSelector(
      this.state.fishnetFeeds.matchInfo,
      this.matchId,
    );
  }

  get outcomesLength() {
    return this.outcomesLengthSelector(
      this.state.fishnetFeeds,
      this.matchId,
      this.marketId,
    );
  }

  get marketNameShort() {
    return this.marketNameShortSelector(
      this.state.fishnetFeeds.markets,
      this.matchId,
      this.marketId,
      getMatchCurPeriod(this.state, this.matchId),
      this.sportId,
      this.lang,
      this.srAlias,
      this.translations,
      this.intl,
    );
  }

  get marketName() {
    return this.marketNameSelector(
      this.state.fishnetFeeds.markets,
      this.matchId,
      this.marketId,
    );
  }

  get hcp() {
    return this.hcpSelector(
      this.state.fishnetFeeds.markets,
      this.matchId,
      this.marketId,
    );
  }

  get betstop() {
    return this.betstopSelector(
      this.state.fishnetFeeds,
      this.matchId,
      this.marketId,
    );
  }
}

export const makeMapStateToProps = (initState, initProps) => {
  // projections is created once when component is mounted
  const {
    useFeedOutcomes, keepAllOutcomes, marketIds, maxMarkets = 1,
  } = initProps;
  const lang = getLanguage(initState);
  const bId = getBId(initState);
  const srAlias = getClientAlias(initState);
  let sportId = getMatchSportId(initState, initProps.matchId);
  const translations = getTranslations(initState);
  const isTranslationsAvailable = getIsTranslationsAvailable(initState);


  let rank = [];
  let mIds = [];

  let marketsNameArr = [];
  let marketsNameShortArr = [];
  let marketsOutcomesArr = [];

  // run every state upt
  return (state, props) => {
    const isLiveOdds = isLive(getMatchStatus(state, initProps.matchId));
    const oddsType = isLiveOdds ? 'live' : 'pre';

    if (marketIds && marketIds.length) {
      mIds = [...marketIds];
    } else {
      if (clientRanking && clientRanking[srAlias] && clientRanking[srAlias][sportId]
        && clientRanking[srAlias][sportId][oddsType]) { // check if custom client ranking for specific sport
        rank = clientRanking[srAlias][sportId][oddsType];
      } else if (sportId === sport.table_tennis) { // no client is setup for TT
        rank = oddsTypeRankingUOF[sportId][oddsType] || getAllOddsTypes(oddsTypeUOF[sportId])[oddsType];
      } else if (clientRanking && clientRanking[srAlias] && clientRanking[srAlias][sport.all]
        && clientRanking[srAlias][sport.all][oddsType]) { // check if custom client ranking for all sports
        rank = clientRanking[srAlias][sport.all][oddsType];
      } else if (oddsTypeRankingUOF[sportId] && oddsTypeRankingUOF[sportId][oddsType]) { // check if app has default ranking for a sport
        rank = oddsTypeRankingUOF[sportId][oddsType];
      } else if (oddsTypeRankingUOF[sport.all] && oddsTypeRankingUOF[sport.all][oddsType]) { // check if app has default ranking for all sports
        rank = oddsTypeRankingUOF[sport.all][oddsType];
      } else if (oddsTypeUOF[sportId] && oddsTypeUOF[sportId][oddsType]) { // check if app has mapping for a sport
        rank = getAllOddsTypes(oddsTypeUOF[sportId])[oddsType]; // auto generated ranking based on the sport's odds type mapping
      } else {
        rank = getAllOddsTypes(oddsTypeUOF[sport.all])[oddsType]; // auto generated ranking based on the all sports odds type mapping
      }

      // allow all active markets just sort based on rank
      // mIds = removeDuplicatesKeepFirst(rank.concat(allOddsTypes));

      // don't allow other than ranked markets
      mIds = rank;
    }

    // Removing duplicates in odds, we could have the same ID for pre-match and live.
    mIds = mIds.filter((id, i, arr) => arr.indexOf(id) === i);
    const intl = createIntlHelper(lang, translations, isTranslationsAvailable);

    let mpArr = mIds.map(marketId => new MarketProjection(
      initState,
      initProps.matchId,
      marketId,
      sportId,
      lang,
      bId,
      srAlias,
      keepAllOutcomes,
      translations,
      intl,
    ));

    // here we update the projection with latest 'state' and 'props', props shouldnt really update
    if (sportId === -1 && !mpArr.length) {
      sportId = getMatchSportId(state, props.matchId);
      mIds = sportId > -1
        ? oddsTypeRankingUOF[sportId][oddsType] || oddsTypeRankingUOF[0][oddsType]
        : [];

      mpArr = mIds.map(marketId => new MarketProjection(
        state,
        props.matchId,
        marketId,
        sportId,
        lang,
        bId,
        srAlias,
        keepAllOutcomes,
        translations,
        intl,
      ));
    }

    // new arr created each update
    const usedMarketsIndices = [];
    const betstopIndices = [];
    let odds = 0;

    for (let i = 0; i < mpArr.length; i++) {
      mpArr[i].update(
        state,
        props.matchId,
        mIds[i],
        getMatchSportId(state, props.matchId),
      );

      if (mpArr[i].betstop && mpArr[i].outcomesLength) {
        betstopIndices.push(i);
      } else if (mpArr[i].outcomesLength) {
        usedMarketsIndices.push(i);
        odds++;
      }
      // early bailout
      if (odds === maxMarkets) {
        break;
      }
    }

    // fill remaining slots with betstopped markets
    for (let j = 0; j < betstopIndices.length && usedMarketsIndices.length < maxMarkets; j++) {
      usedMarketsIndices.push(betstopIndices[j]);
    }

    // .outcomes will trigger outcomes selector
    let marketsOutcomesArrTmp;
    if (useFeedOutcomes) {
      marketsOutcomesArrTmp = usedMarketsIndices.map(idx => mpArr[idx].feedOutcomes);
    } else {
      marketsOutcomesArrTmp = usedMarketsIndices.map(idx => mpArr[idx].outcomes);
    }
    const marketsNameArrTmp = usedMarketsIndices.map(idx => mpArr[idx].marketName);
    const marketsNameShortArrTmp = usedMarketsIndices.map(idx => mpArr[idx].marketNameShort);

    // check if elements in array have changed, if so replace the array to trigger props diff
    // unchanged markets will still have same ref
    marketsNameArr = equalEnough(marketsNameArr, marketsNameArrTmp);
    marketsNameShortArr = equalEnough(marketsNameShortArr, marketsNameShortArrTmp);
    marketsOutcomesArr = equalEnough(marketsOutcomesArr, marketsOutcomesArrTmp);

    return {
      // returns the same arrays if nothing changes
      marketsNameArr,
      marketsNameShortArr,
      marketsOutcomesArr,
    };
  };
};
